import React, { useEffect, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { useProfilePage } from 'screens/Profile/hooks/dataHooks';
import { addMention } from 'screens/home/addPost/mentionHelper';
import { ShareFeedbackBody } from 'screens/performanceReview/components/ContinuousFeedback/Drawers/ShareFeedback/ShareFeedbackBody';
import { useSaveShareFeedback } from 'screens/performanceReview/components/ContinuousFeedback/Drawers/ShareFeedback/dataHooks';
import { Footer } from 'screens/performanceReview/components/ContinuousFeedback/Drawers/common/Footer';
import {
  setClearProvidedFeedbackData,
  setMeshMavenGroupId,
  setPrimaryPanelAction,
  setProvidedFeedbackPost,
  useProvidedFeedbackData,
} from 'screens/performanceReview/components/ContinuousFeedback/Drawers/slice/feedbackSlice';
import { SecondaryPanelAction } from 'screens/performanceReview/components/ContinuousFeedback/Drawers/types/FeedbackSlice';
import { useClearMutationCache } from 'utils/helpers/reactQueryHelpers';
import uuidv4 from 'utils/uuidv4';

import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getEntitiesByLogic } from 'services/helpers/post';

import { Divider } from 'uiToolkitV1/Divider';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import {
  setPrimaryScreen,
  setSecondaryPanelOpen,
  setSecondaryScreen,
  useCompetencyDrawerState,
} from '../competencyDrawerSlice';
import { useGetPostsMutation, useGetLevelUpDetailsQuery } from '../dataHooks';
import { useCompetencyQueryParams } from '../helpers/useCompetencyQueryParams';
import { PrimaryDrawerScreens, SecondaryDrawerScreens } from '../types/slice';

export const ShareFeedbackPanel = () => {
  const dispatch = useDispatch();
  const clearMutationCache = useClearMutationCache();
  const { feedbackPost, feedbackSentimentType, applicableEmployees } = useProvidedFeedbackData();
  const { employeeId, competencyId, isSharePublicPraise: isSharePublicPraiseParam } = useCompetencyQueryParams();
  const [isSharePublicPraise, setSharePublicPraise] = useState(false);

  const { mutate: fetchPosts } = useGetPostsMutation();
  const { isOpen } = useCompetencyDrawerState();
  const { data: competency } = useGetLevelUpDetailsQuery();

  const { data: providerData } = useProfilePage({ employeeId });

  useEffect(() => {
    const isSharePublicPraiseParamBool = isSharePublicPraiseParam === 'true';
    if (isSharePublicPraiseParamBool !== isSharePublicPraise) {
      // adding timeout because to close the panel we remove the current url , it makes the isSharePublicPraise false before panel got closed and render share feedback panel for 1 second which appears wrong
      setTimeout(() => setSharePublicPraise(isSharePublicPraiseParamBool), 500);
    }
  }, [isSharePublicPraiseParam]);

  const openSecondaryPanel = (action: SecondaryPanelAction) => {
    dispatch(setPrimaryScreen(PrimaryDrawerScreens.SHARE_FEEDBACK));
    dispatch(
      setSecondaryScreen(
        action === 'GOAL_SELECTION' ? SecondaryDrawerScreens.GOAL_SELECTION : SecondaryDrawerScreens.MESH_MAVEN
      )
    );
  };

  const closeSecondaryPanel = () => {
    dispatch(setSecondaryPanelOpen(false));
  };

  const onClose = () => {
    clearMutationCache([REACT_QUERY_KEYS.RephraseText]);
    clearMutationCache([REACT_QUERY_KEYS.FeedbackAnalysis]);
    closeSecondaryPanel();
    if (isOpen) {
      fetchPosts({
        feedbackType: feedbackSentimentType === 'APPRECIATIVE' ? 'KUDOS' : 'FEEDBACK',
        limit: 3,
        offset: 0,
        isResetPosts: true,
      });
    }
    setTimeout(() => {
      dispatch(setClearProvidedFeedbackData());
    }, 500);
  };

  const { onSave, isSaving } = useSaveShareFeedback({ source: 'develop_panel', onClose });

  useEffect(() => {
    const employeeToTag = (emp) => {
      const employee = { ...emp };
      employee.name = employee.fullName;
      employee.uuid = employee.id;
      const { metadata } = employee;
      return { employee, name: employee.name, metadata };
    };
    if (providerData && providerData?.id && feedbackPost.getCurrentContent().getPlainText('\n') === '') {
      const editorState = addMention(feedbackPost, employeeToTag(providerData), ' @', '@', true);
      setTimeout(() => {
        dispatch(setProvidedFeedbackPost({ feedbackPost: editorState }));
      }, 1000);
    }
  }, [feedbackPost, providerData, dispatch]);

  useEffect(() => {
    const taggedCompetencies = getEntitiesByLogic({
      editorState: feedbackPost,
      findEntity: ({ type }) => type === '#mention',
    });

    if (competency && competencyId && !taggedCompetencies.length) {
      const editorState = addMention(feedbackPost, { uuid: competencyId, name: competency.name }, ' #', '#', true);
      setTimeout(() => {
        dispatch(setProvidedFeedbackPost({ feedbackPost: editorState }));
      }, 1000);
    }
  }, [competency, competencyId, feedbackPost, dispatch]);

  useEffect(() => {
    dispatch(setPrimaryPanelAction({ primaryPanelAction: 'SHARE' }));
    dispatch(setMeshMavenGroupId({ meshMavenGroupId: uuidv4() }));
  }, [dispatch]);

  return (
    // @ts-expect-error union too complex
    <Flex flexDir="column" borderRadius="8px" bgColor="#FFFFFF" flex={1}>
      <Box p="12px" pb="8px !important">
        {isSharePublicPraise ? (
          <TextBodyMedium bold>Share public praise</TextBodyMedium>
        ) : (
          <TextBodyMedium bold>Share feedback</TextBodyMedium>
        )}
      </Box>
      <Divider borderColor="#CFD7DF" my={3} />
      <Box p={2} mb="auto">
        <ShareFeedbackBody
          openSecondaryPanel={openSecondaryPanel}
          source="develop_panel"
          isSharePublicPraise={isSharePublicPraise}
        />
      </Box>
      <Footer
        onClose={onClose}
        onSave={onSave}
        saveButtonDisabled={isSaving || applicableEmployees?.length === 0}
        saveButtonDisabledReason="Sharing feedback"
      />
    </Flex>
  );
};
